import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { wrap } from '@popmotion/popcorn';
import styled from 'styled-components';
import delay from 'lodash-es/delay';
import withProps from '../../utils/withProps';
import debounce from 'lodash-es/debounce';

const variants = {
  visible: { opacity: 1, zIndex: 4 },
  hidden: { opacity: 0, zIndex: 3 },
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: black;
  overflow: hidden;
`;

const Image = styled(motion.img)`
  position: absolute;
  max-width: 100vw;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

export const AbsoluteBottom = styled.div`
  position: absolute;
  bottom: 2em;
  left: 0;
  right: 0;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Paginator = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;
  left: 0;
  right: 0;
  margin: auto;
  > * {
    z-index: 19;
  }
`;

const Arrow = withProps({
  className: 'material-icons',
  whileHover: { scale: 1.06 },
  whileTap: { scale: 0.95 },
})(styled(motion.i)`
  width: 1.8em;
  height: 1.8em;
  background: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  user-select: none;
  cursor: pointer;
  font-size: 1.2em;

  margin: 0 0.3em;
`);

const Indicator = styled(motion.div)`
  width: 1.1em;
  height: 1.1em;
  border: 2px solid white;
  border-radius: 50%;
  user-select: none;
  margin: 0 0.3em;
  cursor: pointer;
`;

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

export const Slideshow = ({ images, children, style, AbovePagination }) => {
  const [page, setPage] = useState(0);
  const [interaction, setInteraction] = useState(false);

  const imageIndex = wrap(0, images.length, page);

  const paginate = newDirection => {
    setPage(page + newDirection);
  };

  useEffect(() => {
    if (page !== 0) {
      setInteraction(true);
      const timer = delay(setInteraction, 5000, false);
      return () => clearTimeout(timer);
    }
  }, [page, setInteraction]);

  const [drag, setDrag] = useState(true);

  const toggleDrag = (val, opts) =>
    debounce(
      () => {
        console.log('calling', val);
        setDrag(val);
      },
      400,
      opts
    );
  const activateDrag = toggleDrag(true);
  const deactivateDrag = toggleDrag(false, { leading: true });

  return (
    <Container style={style}>
      {images.map((img, i) => (
        <Image
          rel="preload"
          key={i}
          src={img}
          variants={variants}
          initial={i === 0 ? 'visible' : 'hidden'}
          animate={i === imageIndex ? 'visible' : 'hidden'}
          drag={drag ? 'x' : false}
          dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          dragElastic={0.1}
          onDragStart={(e, { offset }) => {
            if (Math.abs(offset.y) > Math.abs(offset.x)) {
              deactivateDrag();
              activateDrag();
            }
          }}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);

            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}
          transition={{ opacity: { duration: 0.5 } }}
        />
      ))}
      <AbsoluteBottom>
        {AbovePagination && <AbovePagination />}
        <Paginator>
          <Arrow onClick={() => paginate(-1)}>chevron_left</Arrow>
          {[...Array(Math.min(images.length, 5))].map((_, i) => (
            <Indicator
              key={i}
              whileHover={{ borderWidth: 3 }}
              initial={{ borderWidth: 2 }}
              onClick={() => setPage(i)}
              style={{ background: i === imageIndex ? 'white' : 'none' }}
            />
          ))}
          <Arrow onClick={() => paginate(1)}>chevron_right</Arrow>
        </Paginator>
      </AbsoluteBottom>
      <motion.div
        initial="visible"
        animate={!interaction ? 'visible' : 'hidden'}
        variants={variants}
        transition={{ opacity: { duration: 1 } }}
        children={children}
        style={{ pointerEvents: 'none' }}
      />
    </Container>
  );
};
